import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  UserPlus,
  User,
  Mail,
  Phone,
  Briefcase,
  Shield,
  FileSpreadsheet,
  PenTool,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const ModalUploadUsersForm = () => {
    const server_app_api_php = window.SERVER_APP_API_PHP;
    let userType = localStorage.getItem('userType');
  const [uploadMethod, setUploadMethod] = useState('');
  const [userDetails, setUserDetails] = useState({
    lname: '',
    fname: '',
    phone: '',
    email: '',
    position: '',
    role: '',
    excelFile: null,
  });
  const [businessLocations, setBusinessLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const userCompanyID = 1;

  useEffect(() => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationOnUploadForm');
    formData.append("companyID", userCompanyID);
    formData.append("location", '');
    
    axios.post(`${server_app_api_php}/server/index.php`, formData)
      .then(response => {
        setBusinessLocations(response.data);
      })
      .catch(error => {
        setError("Failed to load business locations");
      });
  }, []);

  const handleUploadMethodChange = (e) => {
    setUploadMethod(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setUserDetails((prevState) => ({
      ...prevState,
      excelFile: e.target.files[0],
    }));
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    const formData = new FormData();
    formData.append('excelFile', userDetails.excelFile);
    formData.append('companyID', userCompanyID);
    formData.append('businessLocationID', '');
    formData.append('apiType', 'registerUsersExcel');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setLoading(false);
      setSuccess(true);
      setUserDetails((prevState) => ({ ...prevState, excelFile: null }));
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setLoading(false);
      setError('Network error. Please check your internet connection.');
    }
  };

  const handleUserRegistration = async () => {
    const { lname, fname, phone, email, position, role } = userDetails;
    setLoading(true);
    setError('');
    setSuccess(false);

    if (lname && fname && phone && email && position && role) {
      const formData = new FormData();
      formData.append('lname', lname);
      formData.append('fname', fname);
      formData.append('phone', phone);
      formData.append('email', email);
      formData.append('position', position);
      formData.append('role', role);
      formData.append('companyID', userCompanyID);
      formData.append('businessLocationID', '');
      formData.append('apiType', 'registerUsers');

      try {
        const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setLoading(false);
        if (response.data === 'Successful') {
          setSuccess(true);
          setUserDetails({
            lname: '',
            fname: '',
            phone: '',
            email: '',
            position: '',
            role: '',
            excelFile: null,
          });
          setTimeout(() => setSuccess(false), 3000);
        } else {
          setError(response.data);
        }
      } catch (error) {
        setLoading(false);
        setError('Network error. Please check your internet connection.');
      }
    } else {
      setLoading(false);
      setError('Please fill all required fields');
    }
  };

  return (
    <>
      <Header />
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-2">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl">
                  <UserPlus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Register Users</h1>
                  <p className="text-gray-600">Add new system users or bulk upload via Excel</p>
                </div>
              </div>
            </div>

            {/* Status Messages */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">User registered successfully!</span>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800 font-medium">{error}</span>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Upload Method
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setUploadMethod('Single Form')}
                    className={`p-4 rounded-xl border-2 transition-all ${uploadMethod === 'Single Form' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <PenTool className="w-5 h-5 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Single Form</p>
                        <p className="text-sm text-gray-500">Register one user at a time</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setUploadMethod('Upload Excel File')}
                    className={`p-4 rounded-xl border-2 transition-all ${uploadMethod === 'Upload Excel File' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <FileSpreadsheet className="w-5 h-5 text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Excel Upload</p>
                        <p className="text-sm text-gray-500">Bulk upload via Excel file</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {uploadMethod === 'Upload Excel File' && (
                <form onSubmit={handleFileUpload} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excel File
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
                      <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Drag & drop your Excel file here</p>
                      <p className="text-sm text-gray-500 mb-4">or</p>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {userDetails.excelFile && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg">
                          <p className="text-green-800 font-medium">Selected: {userDetails.excelFile.name}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !userDetails.excelFile}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-6 rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Uploading...' : 'Upload Users'}
                  </button>
                </form>
              )}

              {uploadMethod === 'Single Form' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        First Name
                      </label>
                      <input
                        type="text"
                        name="fname"
                        value={userDetails.fname}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter first name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lname"
                        value={userDetails.lname}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter last name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone Number
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={userDetails.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter phone number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={userDetails.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter email address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Briefcase className="w-4 h-4 inline mr-2" />
                        Position
                      </label>
                      <select
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        name="position"
                        value={userDetails.position}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Position</option>
                        {userType === "Admin" && <option value="Director">Director</option>}
                        <option value="Manager">Manager</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Shield className="w-4 h-4 inline mr-2" />
                        Role
                      </label>
                      <select
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        name="role"
                        value={userDetails.role}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Role</option>
                        {userType === "Admin" && <option value="User">User</option>}
                        <option value="Cashier">Cashier</option>
                        <option value="Site_Manager">Site Manager</option>
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={handleUserRegistration}
                    disabled={loading || !userDetails.lname || !userDetails.fname || 
                             !userDetails.phone || !userDetails.email || 
                             !userDetails.position || !userDetails.role}
                    className="w-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white py-3 px-6 rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Registering...' : 'Register User'}
                  </button>
                </div>
              )}

              {!uploadMethod && (
                <div className="text-center py-12">
                  <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select an upload method</h3>
                  <p className="text-gray-500">Choose between single form or Excel upload to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalUploadUsersForm;